import React from 'react'
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import { Box, useTheme, Typography, List, ListItem } from "@mui/material";
import { tokens } from "../../theme";
import Axios from 'axios';
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { getAuthHeader } from '../../utils/authToken';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const grid = 8;

const getItemStyle = (isDragging, draggableStyle, colors) => ({
    userSelect: "none",
    padding: `${grid}px ${grid * 2}px`,
    margin: `0 0 ${grid}px 0`,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: isDragging ? colors.greenAccent[600] : colors.primary[700],
    border: `1px solid ${colors.primary[100]}`,
    ...draggableStyle
});

const getListStyle = (isDraggingOver, colors) => ({
    border: isDraggingOver ? `2px dashed ${colors.greenAccent[500]}` : `2px solid ${colors.primary[100]}`,
    borderRadius: 4,
    padding: grid,
    backgroundColor: colors.primary[600],
});

const ReorderQuestionsModal = ({
    questions,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
    setUpdated,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [questionList, setQuestionList] = React.useState([]);
    const [questionMap, setQuestionMap] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    // for modal
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);

        await Axios.put(`${process.env.REACT_APP_URL}/api/v1/hw-questions/reorder`, {
            orderedIds: questionList,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then(async (response) => {
            setSuccessAlertMessage("Questions reordered successfully");
            setSuccessAlertOpen(true);
            handleClose();
            setUpdated(true);
        }).catch((error) => {
            setErrorAlertMessage(error.response?.data?.message || "Error: Cannot reorder questions. Please try again.");
            setErrorAlertOpen(true);
        }).finally(() => {
            setSaving(false);
        });
    };

    React.useEffect(() => {
        if (!questions || questions.length === 0 || !open) return;

        setLoading(true);

        const qMap = {};
        for (const question of questions) {
            qMap[question.id] = question;
        }

        const list = questions.map((question) => question.id);
        // sort by order_in_page if available, otherwise by question_label
        list.sort((a, b) => {
            const orderA = qMap[a].order_in_page;
            const orderB = qMap[b].order_in_page;
            if (orderA !== null && orderB !== null) {
                return orderA - orderB;
            }
            if (orderA !== null) return -1;
            if (orderB !== null) return 1;
            return (qMap[a].question_label || '').localeCompare(qMap[b].question_label || '');
        });

        setQuestionList(list);
        setQuestionMap(qMap);

        setLoading(false);
    }, [questions, open]);

    const handleOnDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination) return;

        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        const items = Array.from(questionList);
        const [removed] = items.splice(source.index, 1);
        items.splice(destination.index, 0, removed);

        setQuestionList(items);
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        maxHeight: "80vh",
        overflowY: "auto",
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        borderRadius: '8px',
    };

    return (
        <div>
            <Button
                type="button"
                variant="contained"
                onClick={handleOpen}
                style={{
                    backgroundColor: colors.primary[300],
                    color: "white",
                    minHeight: "2.5rem"
                }}
                size='small'
            >
                Reorder Questions
            </Button>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h6" fontWeight="bold" color={colors.primary[450]}>
                            Reorder Questions
                        </Typography>
                        <Typography variant="body2" color={colors.primary[300]} mt={1}>
                            Drag and drop to reorder the questions on this page.
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2} width="100%" pt={2}>
                            <Box display="flex" flexDirection="column">
                                {loading ? (
                                    <Typography>Loading...</Typography>
                                ) : questions === null || questions.length === 0 ? (
                                    <Typography color={colors.primary[450]}>No questions available.</Typography>
                                ) : (
                                    <DragDropContext onDragEnd={handleOnDragEnd}>
                                        <Droppable
                                            droppableId="droppable"
                                            renderClone={(provided, snapshot, rubric) => (
                                                <ListItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style,
                                                        colors
                                                    )}
                                                >
                                                    <DragIndicatorIcon />
                                                    Question
                                                </ListItem>
                                            )}
                                        >
                                            {(provided, snapshot) => (
                                                <List
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    style={getListStyle(snapshot.isDraggingOver, colors)}
                                                >
                                                    {questionList && questionList.length > 0 && questionList.map((questionId, index) => {
                                                        const question = questionMap[questionId];

                                                        return (
                                                            <Draggable
                                                                key={questionId}
                                                                draggableId={questionId.toString()}
                                                                index={index}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <ListItem
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        style={getItemStyle(
                                                                            snapshot.isDragging,
                                                                            provided.draggableProps.style,
                                                                            colors
                                                                        )}
                                                                    >
                                                                        <DragIndicatorIcon sx={{ color: colors.primary[300] }} />
                                                                        <Typography sx={{ wordBreak: 'break-word' }}>
                                                                            {question?.question_label}
                                                                        </Typography>
                                                                    </ListItem>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </List>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </Box>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                sx={{ width: "fit-content" }}
                                onClick={handleSave}
                                disabled={!questionList || questionList.length === 0 || saving}
                            >
                                {saving ? "Saving..." : "Save Order"}
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    )
}

export default ReorderQuestionsModal
